<template>
  <b-modal
    v-model="showModal"
    hide-header
    hide-footer
    centered
    dialog-class="mc-carrinho-dialog"
    content-class="mc-carrinho-content-wrap"
    body-class="mc-carrinho-modal-body modal-body-carrinho"
    @hidden="teardownInfiniteScrollProdutos"
    @shown="onModalShown"
  >
    <div class="mc-carrinho-root">
      <!-- Header fixo -->
      <header class="mc-carrinho-header">
        <div class="mc-carrinho-header-main">
          <div class="mc-carrinho-header-title-row">
            <div class="mc-carrinho-header-icon-wrap">
              <i class="feather icon-shopping-cart" />
            </div>
            <div class="mc-carrinho-header-texts">
              <h2 class="mc-carrinho-title">
                Criar Carrinho de Compras
              </h2>
              <p class="mc-carrinho-subtitle">
                Monte um checkout e envie para o cliente
              </p>
            </div>
            <div class="mc-carrinho-header-meta">
              <span class="mc-badge mc-badge-items">{{ textoBadgeItens }}</span>
              <span class="mc-header-total">{{ subtotalFormatado }}</span>
            </div>
          </div>
          <div class="mc-carrinho-header-actions">
            <b-button
              variant="outline-danger"
              size="sm"
              class="mc-btn-outline-danger"
              :disabled="carrinho.length === 0"
              @click="limparCarrinho"
            >
              Limpar carrinho
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              class="mc-btn-outline-copy"
              :disabled="carrinho.length === 0 || !linkCarrinhoGerado"
              @click="copiarLink"
            >
              <i class="feather icon-copy" />
              Copiar link
            </b-button>
            <b-button
              variant="primary"
              size="sm"
              class="mc-btn-cta-header"
              :disabled="carrinho.length === 0 || !linkCarrinhoGerado"
              @click="abrirLink"
            >
              <i class="feather icon-external-link" />
              Abrir link
            </b-button>
          </div>
        </div>
        <button
          type="button"
          class="mc-carrinho-close"
          aria-label="Fechar"
          @click="fecharModal"
        >
          <i class="feather icon-x" />
        </button>
      </header>

      <div class="mc-carrinho-grid">
        <!-- Coluna esquerda: busca + lista -->
        <div class="mc-carrinho-col mc-carrinho-col-left">
          <div class="mc-search-block">
            <div class="mc-search-bar">
              <i class="feather icon-search mc-search-icon" />
              <input
                v-model="termoBusca"
                type="search"
                class="mc-search-input"
                :placeholder="buscarPorSku ? 'Digite o código SKU…' : 'Buscar produto, SKU ou coleção…'"
                @keyup.enter="buscarProdutos"
              >
              <b-button
                variant="primary"
                size="sm"
                class="mc-search-submit"
                :disabled="buscandoProdutos"
                @click="buscarProdutos"
              >
                <b-spinner v-if="buscandoProdutos" small />
                <span v-else>Buscar</span>
              </b-button>
            </div>
            <button
              type="button"
              class="mc-sku-toggle"
              :class="{ 'mc-sku-toggle--on': buscarPorSku }"
              @click="buscarPorSku = !buscarPorSku"
            >
              <span class="mc-sku-toggle-track">
                <span class="mc-sku-toggle-thumb" />
              </span>
              <span>Buscar por SKU</span>
            </button>
          </div>

          <div class="mc-chips-row" role="tablist" aria-label="Filtros rápidos">
            <button
              v-for="chip in chipsFiltros"
              :key="chip.id"
              type="button"
              role="tab"
              class="mc-chip"
              :class="{ 'mc-chip--active': filtroRapido === chip.id }"
              :aria-selected="filtroRapido === chip.id"
              @click="filtroRapido = chip.id"
            >
              {{ chip.label }}
            </button>
          </div>

          <p
            v-if="produtosEncontrados.length > 0"
            class="mc-resultados-hint"
          >
            Mostrando resultados
          </p>

          <div
            ref="scrollListaProdutos"
            class="mc-lista-produtos-scroll"
          >
            <template v-if="buscandoProdutos && produtosEncontrados.length === 0">
              <div
                v-for="n in 6"
                :key="`sk-${n}`"
                class="mc-prod-skeleton"
              >
                <div class="mc-prod-skeleton-img" />
                <div class="mc-prod-skeleton-lines">
                  <div class="mc-prod-skeleton-line mc-prod-skeleton-line--lg" />
                  <div class="mc-prod-skeleton-line mc-prod-skeleton-line--sm" />
                </div>
              </div>
            </template>

            <div
              v-else-if="buscaJaRealizada && produtosEncontrados.length === 0"
              class="mc-empty-state mc-empty-state--produtos"
            >
              <i class="feather icon-package" />
              <p class="mc-empty-title">
                Nenhum produto encontrado
              </p>
              <p class="mc-empty-hint">
                Tente buscar por nome, SKU ou coleção.
              </p>
            </div>

            <template v-else>
              <div
                v-for="produto in produtosPaginados"
                :key="produto.id"
                class="mc-prod-row"
              >
                <img
                  :src="produto.imagem"
                  :alt="produto.nome"
                  class="mc-prod-row-img"
                >
                <div class="mc-prod-row-body">
                  <a
                    v-if="produto.link"
                    :href="getLinkCompleto(produto.link)"
                    target="_blank"
                    rel="noopener"
                    class="mc-prod-row-name mc-prod-row-name--link"
                    :title="produto.nome"
                  >{{ produto.nome }}</a>
                  <div
                    v-else
                    class="mc-prod-row-name"
                    :title="produto.nome"
                  >
                    {{ produto.nome }}
                  </div>
                  <div class="mc-prod-row-sku">
                    SKU: {{ produto.codigo }}
                  </div>
                  <div class="mc-prod-row-meta">
                    <span class="mc-prod-row-preco">R$ {{ produto.preco.toFixed(2) }}</span>
                    <span class="mc-prod-row-estoque">Estoque: {{ produto.estoque }}</span>
                  </div>
                </div>
                <div class="mc-prod-row-actions">
                  <div class="mc-qty-inline" aria-label="Quantidade a adicionar">
                    <button
                      type="button"
                      class="mc-qty-inline-btn"
                      :disabled="(qtdAdicionarBusca[produto.id] || 1) <= 1"
                      @click="ajustarQtdBusca(produto.id, -1)"
                    >
                      <i class="feather icon-minus" />
                    </button>
                    <span class="mc-qty-inline-val">{{ qtdAdicionarBusca[produto.id] || 1 }}</span>
                    <button
                      type="button"
                      class="mc-qty-inline-btn"
                      @click="ajustarQtdBusca(produto.id, 1)"
                    >
                      <i class="feather icon-plus" />
                    </button>
                  </div>
                  <b-button
                    size="sm"
                    variant="primary"
                    class="mc-btn-add"
                    @click="adicionarAoCarrinho(produto)"
                  >
                    Adicionar
                  </b-button>
                </div>
              </div>

              <div
                v-if="produtosEncontrados.length > 0 && paginaAtual < totalPaginas"
                ref="sentinelProdutos"
                class="mc-sentinel-load"
              >
                <b-spinner small class="mc-sentinel-spinner" />
                <span>Carregando mais produtos…</span>
              </div>
            </template>
          </div>
        </div>

        <!-- Coluna direita: carrinho, resumo, link, preview -->
        <aside class="mc-carrinho-col mc-carrinho-col-right">
          <div class="mc-right-inner">
            <div class="mc-cart-head">
              <h3 class="mc-cart-title">
                Seu carrinho ({{ totalItensCarrinho }} {{ totalItensCarrinho === 1 ? 'item' : 'itens' }})
              </h3>
              <button
                type="button"
                class="mc-link-quiet"
                :disabled="carrinho.length === 0"
                @click="limparCarrinho"
              >
                Limpar
              </button>
            </div>

            <div class="mc-produtos-no-carrinho-wrap">
              <h4 class="mc-produtos-no-carrinho-titulo">
                Produtos no carrinho
              </h4>
              <div
                v-if="carrinho.length === 0"
                class="mc-empty-produtos-carrinho"
              >
                <p class="mc-empty-produtos-carrinho-title">
                  Nenhum produto no carrinho
                </p>
                <p class="mc-empty-produtos-carrinho-hint">
                  Adicione produtos para gerar o link.
                </p>
              </div>
              <div
                v-else
                class="mc-cart-produtos-scroll"
              >
                <div
                  v-for="produto in carrinho"
                  :key="produto.id"
                  class="mc-prod-carrinho-row"
                >
                  <img
                    v-if="urlImagemExibicaoCarrinho(produto) && !imgCarrinhoQuebrada[produto.id]"
                    :src="urlImagemExibicaoCarrinho(produto)"
                    :alt="produto.nome"
                    class="mc-prod-carrinho-img"
                    @error="marcarImagemCarrinhoQuebrada(produto.id)"
                  >
                  <div
                    v-else
                    class="mc-prod-carrinho-img mc-prod-carrinho-img--placeholder"
                    role="img"
                    :aria-label="'Sem imagem: ' + (produto.nome || 'produto')"
                  >
                    <i class="feather icon-image" />
                  </div>
                  <div class="mc-prod-carrinho-body">
                    <a
                      v-if="produto.link"
                      :href="getLinkCompleto(produto.link)"
                      target="_blank"
                      rel="noopener"
                      class="mc-prod-carrinho-name mc-prod-carrinho-name--link"
                    >{{ produto.nome }}</a>
                    <div
                      v-else
                      class="mc-prod-carrinho-name"
                    >
                      {{ produto.nome }} oi
                    </div>
                    <div class="mc-prod-carrinho-sku">
                      SKU: {{ produto.codigo }}
                    </div>
                    <div class="mc-prod-carrinho-preco-linha">
                      {{ formatarMoedaBr(produto.preco) }} × {{ produto.quantidade || 1 }}
                    </div>
                    <div class="mc-prod-carrinho-subtotal">
                      Subtotal: {{ formatarMoedaBr(subtotalLinhaCarrinho(produto)) }}
                    </div>
                  </div>
                  <div class="mc-prod-carrinho-aside">
                    <div class="mc-qty-inline mc-qty-inline--cart">
                      <b-button
                        size="sm"
                        variant="outline-secondary"
                        class="mc-qty-btn"
                        @click="diminuirQuantidade(produto.id)"
                      >
                        <i class="feather icon-minus" />
                      </b-button>
                      <span class="mc-qty-num">{{ produto.quantidade }}</span>
                      <b-button
                        size="sm"
                        variant="outline-secondary"
                        class="mc-qty-btn"
                        @click="aumentarQuantidade(produto.id)"
                      >
                        <i class="feather icon-plus" />
                      </b-button>
                    </div>
                    <button
                      type="button"
                      class="mc-cart-remove"
                      title="Remover"
                      @click="removerDoCarrinho(produto.id)"
                    >
                      <i class="feather icon-x" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <template v-if="carrinho.length > 0">
              <div class="mc-resumo-pedido">
                <h4 class="mc-section-label">
                  Resumo do pedido
                </h4>
                <div class="mc-resumo-line">
                  <span>Subtotal</span>
                  <span>{{ subtotalFormatado }}</span>
                </div>
                <div
                  v-if="cupomDescontoAplicavelResumo"
                  class="mc-resumo-line"
                >
                  <span>Desconto (cupom {{ percentualCupomNumerico }}%)</span>
                  <span class="mc-resumo-desconto">− {{ valorDescontoCupomFormatado }}</span>
                </div>
                <div class="mc-resumo-line">
                  <span>Frete</span>
                  <span class="mc-resumo-muted">calculado no checkout</span>
                </div>
                <div class="mc-resumo-line mc-resumo-total">
                  <span>Total</span>
                  <span>{{ totalExibicaoResumoFormatado }}</span>
                </div>
              </div>

              <div class="mc-section">
                <h4 class="mc-section-label">
                  Personalizar link
                </h4>
                <div class="mc-field">
                  <label class="mc-field-label" for="mc-cep-input">CEP do cliente (opcional)</label>
                  <div class="mc-field-input-wrap">
                    <i class="feather icon-map-pin mc-field-icon" />
                    <b-form-input
                      id="mc-cep-input"
                      v-model="cepCliente"
                      class="mc-field-input"
                      placeholder="00000-000"
                      @input="formatarCEP"
                    />
                  </div>
                  <p class="mc-field-hint">
                    Frete será calculado no checkout do cliente.
                  </p>
                </div>
                <div class="mc-field">
                  <label class="mc-field-label" for="mc-cupom-input">Cupom de desconto (opcional)</label>
                  <div class="mc-field-input-wrap">
                    <i class="feather icon-tag mc-field-icon" />
                    <b-form-input
                      id="mc-cupom-input"
                      v-model="cupomDesconto"
                      class="mc-field-input"
                      placeholder="Digite o código do cupom"
                    />
                  </div>
                  <p class="mc-field-hint">
                    O cupom será incluído automaticamente no link.
                  </p>
                </div>
                <div
                  v-if="temCupomPreenchido"
                  class="mc-cupom-desconto-extra"
                >
                  <b-form-checkbox
                    v-model="cupomTemDesconto"
                    class="mc-checkbox-cupom-desconto"
                  >
                    Este cupom tem desconto percentual sobre o total do pedido
                  </b-form-checkbox>
                  <div
                    v-if="cupomTemDesconto"
                    class="mc-field mc-field--pct"
                  >
                    <label
                      class="mc-field-label"
                      for="mc-cupom-pct-input"
                    >Percentual de desconto (%)</label>
                    <b-form-input
                      id="mc-cupom-pct-input"
                      v-model="cupomPercentualDesconto"
                      class="mc-field-input"
                      type="text"
                      inputmode="decimal"
                      placeholder="ex.: 10"
                      @blur="normalizarPercentualCupom"
                    />
                    <p class="mc-field-hint">
                      Informe um valor entre 0 e 100. O desconto é aplicado sobre o subtotal do carrinho apenas para exibição na mensagem e no resumo.
                    </p>
                  </div>
                </div>
              </div>

              <div class="mc-section">
                <h4 class="mc-section-label">
                  Link do carrinho
                </h4>
                <div class="mc-link-preview-box">
                  <div class="mc-link-url" :title="linkCarrinhoGerado">
                    {{ linkCarrinhoGerado }}
                  </div>
                  <div class="mc-link-mini-actions">
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      @click="copiarLink"
                    >
                      Copiar
                    </b-button>
                    <b-button
                      size="sm"
                      variant="primary"
                      @click="abrirLink"
                    >
                      Abrir
                    </b-button>
                  </div>
                </div>
                <div class="mc-link-badges">
                  <span class="mc-mini-badge">{{ totalItensCarrinho }} {{ totalItensCarrinho === 1 ? 'item' : 'itens' }}</span>
                  <span
                    v-if="cupomDesconto && cupomDesconto.trim() !== ''"
                    class="mc-mini-badge mc-mini-badge--cupom"
                  >Cupom aplicado</span>
                  <span
                    v-if="cepInformadoCompleto"
                    class="mc-mini-badge mc-mini-badge--cep"
                  >CEP informado</span>
                </div>
              </div>
            </template>

            <div class="mc-section mc-section--preview">
              <div class="mc-preview-msg-head">
                <h4 class="mc-section-label mc-section-label--preview">
                  Prévia da mensagem para o cliente
                </h4>
                <b-button
                  variant="link"
                  size="sm"
                  class="mc-btn-editar-msg"
                  @click="abrirEdicaoMensagem"
                >
                  Editar mensagem
                </b-button>
              </div>
              <div class="mc-wa-preview">
                <div class="mc-wa-bubble">
                  <pre class="mc-wa-bubble-text">{{ textoPreviewMensagem }}</pre>
                </div>
              </div>
              <b-form-checkbox
                v-model="enviarAutoChat"
                class="mc-checkbox-auto"
              >
                Enviar automaticamente no chat
              </b-form-checkbox>
            </div>

            <b-button
              variant="primary"
              block
              size="lg"
              class="mc-btn-wa-footer"
              :disabled="carrinho.length === 0 || !linkCarrinhoGerado"
              @click="enviarCarrinhoWhatsApp"
            >
              <i class="feather icon-message-circle mr-1" />
              Enviar carrinho no WhatsApp
            </b-button>
          </div>
        </aside>
      </div>

      <b-modal
        v-model="modalEdicaoMensagemVisivel"
        title="Editar mensagem"
        size="md"
        centered
        hide-footer
        @hidden="onModalEdicaoMensagemHidden"
      >
        <div class="mc-edit-msg-hint">
          <p class="mc-edit-msg-hint-lead">
            Inclua no texto as variáveis abaixo <strong>exatamente</strong> como aparecem na primeira coluna (com chaves duplas). Na prévia e na mensagem enviada ao cliente, elas são trocadas pelos dados atuais do carrinho e pelo link.
          </p>
          <p class="mc-edit-msg-hint-sub">
            Variáveis disponíveis:
          </p>
          <ul class="mc-edit-msg-vars-list">
            <li
              v-for="row in variaveisMensagemCarrinhoDoc"
              :key="row.token"
              class="mc-edit-msg-vars-item"
            >
              <code class="mc-edit-msg-var-code">{{ row.token }}</code>
              <span class="mc-edit-msg-var-desc">{{ row.desc }}</span>
            </li>
          </ul>
          <p class="mc-edit-msg-hint-note">
            A mensagem que você salvar fica gravada neste navegador e vale para <strong>todas as conversas</strong>. Ao editar e salvar, o novo texto passa a ser o padrão em todos os carrinhos abertos daqui em diante. Se deixar o texto em branco ao salvar, volta a usar o modelo padrão do sistema.
          </p>
        </div>
        <b-form-textarea
          v-model="mensagemEdicaoTemp"
          rows="10"
          class="mc-edit-msg-textarea"
        />
        <div class="mc-edit-msg-footer">
          <b-button
            variant="outline-secondary"
            @click="cancelarEdicaoMensagem"
          >
            Cancelar
          </b-button>
          <b-button
            variant="primary"
            @click="salvarEdicaoMensagem"
          >
            Salvar mensagem
          </b-button>
        </div>
      </b-modal>
    </div>
  </b-modal>
</template>

<script>
import { buscarProdutos, getStore } from '../../functions/basics/chat'

export default {
  name: 'ModalCarrinho',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    clienteId: {
      type: [Number, String],
      required: true,
    },
    uuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      termoBusca: '',
      buscarPorSku: false,
      buscandoProdutos: false,
      produtosEncontrados: [],
      carrinho: [],
      cepCliente: '',
      cupomDesconto: '',
      /** Cupom com desconto percentual explícito (só exibe % e aplica no resumo/prévia quando marcado). */
      cupomTemDesconto: false,
      cupomPercentualDesconto: '',
      paginaAtual: 1,
      itensPorPagina: 10,
      store: '',
      buscaJaRealizada: false,
      filtroRapido: 'todos',
      chipsFiltros: [
        { id: 'todos', label: 'Todos' },
        { id: 'estoque', label: 'Em estoque' },
      ],
      qtdAdicionarBusca: {},
      /** Mensagem salva pelo atendente (substitui o template padrão na prévia e no fluxo futuro de envio). */
      mensagemCustomizadaCarrinho: '',
      mensagemEdicaoTemp: '',
      modalEdicaoMensagemVisivel: false,
      enviarAutoChat: false,
      /** ids de itens cuja URL de imagem falhou ao carregar (exibe placeholder). */
      imgCarrinhoQuebrada: {},
    }
  },
  computed: {
    showModal: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    totalPaginas() {
      return Math.ceil(this.produtosEncontrados.length / this.itensPorPagina) || 1
    },
    produtosPaginados() {
      const fim = this.paginaAtual * this.itensPorPagina
      return this.produtosEncontrados.slice(0, fim)
    },
    totalItensCarrinho() {
      return this.carrinho.reduce((acc, p) => acc + (p.quantidade || 1), 0)
    },
    textoBadgeItens() {
      const n = this.totalItensCarrinho
      if (n === 0) return '0 itens'
      return n === 1 ? '1 item' : `${n} itens`
    },
    subtotalValor() {
      return this.carrinho.reduce(
        (acc, p) => acc + (Number(p.preco) || 0) * (p.quantidade || 1),
        0,
      )
    },
    subtotalFormatado() {
      if (this.carrinho.length === 0) return 'R$ 0,00'
      return `R$ ${this.subtotalValor.toFixed(2).replace('.', ',')}`
    },
    temCupomPreenchido() {
      return !!(this.cupomDesconto && String(this.cupomDesconto).trim())
    },
    percentualCupomNumerico() {
      const raw = String(this.cupomPercentualDesconto || '').trim().replace(',', '.')
      const n = parseFloat(raw)
      if (Number.isNaN(n) || n < 0) return 0
      return Math.min(100, n)
    },
    /** Cupom com código + opção de desconto em % e valor > 0. */
    cupomDescontoAplicavelResumo() {
      return this.temCupomPreenchido && this.cupomTemDesconto && this.percentualCupomNumerico > 0
    },
    valorDescontoCupomReais() {
      if (!this.cupomDescontoAplicavelResumo) return 0
      return this.subtotalValor * (this.percentualCupomNumerico / 100)
    },
    valorTotalAposDescontoCupom() {
      return Math.max(0, this.subtotalValor - this.valorDescontoCupomReais)
    },
    valorDescontoCupomFormatado() {
      return this.formatarMoedaBr(this.valorDescontoCupomReais)
    },
    totalExibicaoResumoFormatado() {
      return this.formatarMoedaBr(this.valorTotalAposDescontoCupom)
    },
    textoPercentualCupomPreview() {
      if (!this.cupomDescontoAplicavelResumo) return ''
      const n = this.percentualCupomNumerico
      const s = Number.isInteger(n) ? String(n) : String(n).replace('.', ',')
      return `${s}%`
    },
    totalComDescontoFormatadoPreview() {
      return this.formatarMoedaBr(this.valorTotalAposDescontoCupom)
    },
    /** CEP com 8 dígitos (mesma regra usada no link) para badge "CEP informado". */
    cepInformadoCompleto() {
      return this.cepCliente.replace(/\D/g, '').length === 8
    },
    /** Bloco de texto com um item por linha para o placeholder {{produtos_do_carrinho}}. */
    textoListaProdutosCarrinho() {
      if (!this.carrinho.length) return ''
      return this.carrinho.map(p => {
        const q = p.quantidade || 1
        const unit = Number(p.preco) || 0
        return `• ${p.nome} — ${q}x — ${this.formatarMoedaBr(unit)}`
      }).join('\n')
    },
    /** Descrição das variáveis para o modal "Editar mensagem". */
    variaveisMensagemCarrinhoDoc() {
      return [
        {
          token: '{{produtos_do_carrinho}}',
          desc: 'Lista dos itens: nome, quantidade e preço unitário em cada linha.',
        },
        {
          token: '{{total_pedido}}',
          desc: 'Subtotal do pedido (soma dos produtos no carrinho), em reais.',
        },
        {
          token: '{{total_com_desconto}}',
          desc: 'Total após o desconto em % do cupom. Só recebe valor quando houver cupom, a opção “tem desconto” marcada e o percentual informado; caso contrário fica vazio.',
        },
        {
          token: '{{percentual_cupom}}',
          desc: 'Texto do percentual (ex.: 10%). Mesmas condições de total_com_desconto; vazio quando não houver % aplicável.',
        },
        {
          token: '{{link}}',
          desc: 'URL do checkout com os produtos do carrinho e, se houver, CEP e cupom.',
        },
      ]
    },
    linkCarrinhoGerado() {
      if (this.carrinho.length === 0) return ''

      const skus = this.carrinho.map(p => `${p.codigo};${p.quantidade || 1}`).join(',')

      const rawSite = String((this.store && this.store.site_url) || '').trim()
      if (!rawSite) return ''
      const baseUrl = rawSite.endsWith('/') ? rawSite : `${rawSite}/`
      let link = `${baseUrl}checkout/cart?type=dynamic&skus=${skus}&action=clear`

      console.log('Store:', this.store)

      const cepSomenteNumeros = this.cepCliente.replace(/\D/g, '')
      console.log('CEP digitado:', this.cepCliente, 'Somente números:', cepSomenteNumeros, 'Tamanho:', cepSomenteNumeros.length)

      if (cepSomenteNumeros.length === 8) {
        link += `&cep=${this.cepCliente}`
        console.log('CEP adicionado ao link:', this.cepCliente)
      }

      if (this.cupomDesconto && this.cupomDesconto.trim() !== '') {
        link += `&cupom-desconto=${encodeURIComponent(this.cupomDesconto.trim())}`
        console.log('Cupom adicionado ao link:', this.cupomDesconto)
      }

      console.log('Link gerado:', link)
      return link
    },
    textoPreviewMensagem() {
      const tpl = (this.mensagemCustomizadaCarrinho && this.mensagemCustomizadaCarrinho.trim() !== '')
        ? this.mensagemCustomizadaCarrinho
        : this.getDefaultMensagemTemplate()
      return this.aplicarPlaceholdersNoTemplate(tpl)
    },
  },
  watch: {
    cepCliente(newVal) {
      console.log('CEP mudou para:', newVal)
      this.salvarCarrinhoNoCache()
    },
    carrinho: {
      handler() {
        this.salvarCarrinhoNoCache()
      },
      deep: true,
    },
    cupomDesconto(val) {
      if (!val || !String(val).trim()) {
        this.cupomTemDesconto = false
        this.cupomPercentualDesconto = ''
      }
      this.salvarCarrinhoNoCache()
    },
    cupomTemDesconto() {
      this.salvarCarrinhoNoCache()
    },
    cupomPercentualDesconto() {
      this.salvarCarrinhoNoCache()
    },
    value(isOpen) {
      if (isOpen) {
        this.restaurarCarrinhoDoCache()
        this.$nextTick(() => {
          this.reaplicarImagensCarrinho()
        })
      }
    },
    clienteId() {
      this.mensagemEdicaoTemp = ''
      this.restaurarCarrinhoDoCache()
      this.$nextTick(() => {
        this.reaplicarImagensCarrinho()
      })
    },
    produtosEncontrados() {
      this.paginaAtual = 1
      this.$nextTick(() => this.setupInfiniteScrollProdutos())
    },
    paginaAtual() {
      this.$nextTick(() => this.setupInfiniteScrollProdutos())
    },
  },
  created() {
    /** @type {null | IntersectionObserver} */
    this.observerListaProdutos = null
  },
  async mounted() {
    this.store = await getStore(this.$route.params.uuid)
    this.restaurarCarrinhoDoCache()
    this.$nextTick(() => {
      this.reaplicarImagensCarrinho()
    })
  },
  beforeDestroy() {
    this.teardownInfiniteScrollProdutos()
  },
  methods: {
    formatarMoedaBr(valor) {
      const n = Number(valor) || 0
      return `R$ ${n.toFixed(2).replace('.', ',')}`
    },
    aplicarPlaceholdersNoTemplate(tpl) {
      if (!tpl) return ''
      const totalComDescontoTxt = this.cupomDescontoAplicavelResumo
        ? this.totalComDescontoFormatadoPreview
        : ''
      let s = tpl
      s = s.split('{{produtos_do_carrinho}}').join(this.textoListaProdutosCarrinho)
      s = s.split('{{total_com_desconto}}').join(totalComDescontoTxt)
      s = s.split('{{total_pedido}}').join(this.subtotalFormatado)
      s = s.split('{{percentual_cupom}}').join(this.textoPercentualCupomPreview)
      s = s.split('{{link}}').join(this.linkCarrinhoGerado || '')
      return s.replace(/\n{3,}/g, '\n\n')
    },
    normalizarPercentualCupom() {
      const raw = String(this.cupomPercentualDesconto || '').trim().replace(',', '.')
      if (raw === '') return
      const n = parseFloat(raw)
      if (Number.isNaN(n)) {
        this.cupomPercentualDesconto = ''
        return
      }
      const clamped = Math.min(100, Math.max(0, n))
      this.cupomPercentualDesconto = Number.isInteger(clamped)
        ? String(clamped)
        : String(clamped).replace('.', ',')
    },
    /**
     * Campo de imagem no retorno de buscarProdutos (chat.js): `imagem` (derivacao.imagem / produto.imagem / imagemPrincipal).
     * Aceita aliases comuns para compatibilidade com payloads antigos ou cache legado.
     */
    extrairUrlImagemBruta(produto) {
      if (!produto || typeof produto !== 'object') return null
      const v = produto.imagem
        || produto.image
        || produto.image_url
        || produto.picture
        || produto.url_imagem
        || produto.imagemPrincipal
      if (v == null) return null
      const s = String(v).trim()
      return s === '' ? null : s
    },
    absolutizarUrlImagemMidia(url) {
      if (!url || typeof url !== 'string') return null
      const u = url.trim()
      if (u === '') return null
      if (u.startsWith('http://') || u.startsWith('https://')) return u
      if (u.startsWith('//')) {
        const proto = typeof window !== 'undefined' && window.location && window.location.protocol
          ? window.location.protocol
          : 'https:'
        return `${proto}${u}`
      }
      const base = this.store && this.store.site_url ? String(this.store.site_url).replace(/\/$/, '') : ''
      if (!base) return u
      const path = u.startsWith('/') ? u : `/${u}`
      return `${base}${path}`
    },
    /** URL final para exibir no carrinho (absoluta quando a loja for conhecida). */
    urlImagemExibicaoCarrinho(produto) {
      const bruta = this.extrairUrlImagemBruta(produto)
      if (!bruta) return ''
      const abs = this.absolutizarUrlImagemMidia(bruta)
      return abs && String(abs).trim() !== '' ? String(abs).trim() : ''
    },
    /**
     * Garante objeto de item de carrinho com `imagem` persistível (URL absoluta quando possível)
     * e demais campos preservados — usado ao adicionar, ao salvar e ao restaurar do cache.
     */
    montarItemProdutoCarrinho(produto, quantidade) {
      const qtd = quantidade != null ? quantidade : (produto.quantidade || 1)
      const bruta = this.extrairUrlImagemBruta(produto)
      const imagemAbsoluta = bruta ? this.absolutizarUrlImagemMidia(bruta) : null
      return {
        ...produto,
        quantidade: qtd,
        imagem: imagemAbsoluta || bruta || null,
      }
    },
    reaplicarImagensCarrinho() {
      if (!this.carrinho || this.carrinho.length === 0) return
      this.carrinho = this.carrinho.map(p => this.montarItemProdutoCarrinho(p, p.quantidade || 1))
    },
    marcarImagemCarrinhoQuebrada(produtoId) {
      this.$set(this.imgCarrinhoQuebrada, produtoId, true)
    },
    subtotalLinhaCarrinho(produto) {
      return (Number(produto.preco) || 0) * (produto.quantidade || 1)
    },
    /** Template padrão; a linha do total com cupom só entra quando houver % de cupom aplicável. */
    getDefaultMensagemTemplate() {
      const linhaTotalCupom = this.cupomDescontoAplicavelResumo
        ? '\nTotal com cupom: {{total_com_desconto}}'
        : ''
      return `Preparei seu carrinho com os produtos escolhidos:\n\n{{produtos_do_carrinho}}\n\nTotal do pedido: {{total_pedido}}${linhaTotalCupom}\n\nFinalize sua compra pelo link:\n{{link}}`
    },
    abrirEdicaoMensagem() {
      this.mensagemEdicaoTemp = (this.mensagemCustomizadaCarrinho && this.mensagemCustomizadaCarrinho.trim() !== '')
        ? this.mensagemCustomizadaCarrinho
        : this.getDefaultMensagemTemplate()
      this.modalEdicaoMensagemVisivel = true
    },
    salvarEdicaoMensagem() {
      this.mensagemCustomizadaCarrinho = (this.mensagemEdicaoTemp || '').trim()
      this.modalEdicaoMensagemVisivel = false
      localStorage.setItem('carrinho_mensagem_global', this.mensagemCustomizadaCarrinho)
      this.salvarCarrinhoNoCache()
    },
    cancelarEdicaoMensagem() {
      this.modalEdicaoMensagemVisivel = false
    },
    onModalEdicaoMensagemHidden() {
      this.mensagemEdicaoTemp = ''
    },
    onModalShown() {
      this.$nextTick(() => this.setupInfiniteScrollProdutos())
    },
    /**
     * Carrega mais itens da lista local quando o sentinela entra na área visível da lista (substitui a paginação numérica).
     */
    setupInfiniteScrollProdutos() {
      this.teardownInfiniteScrollProdutos()
      const root = this.$refs.scrollListaProdutos
      const sentinel = this.$refs.sentinelProdutos
      if (!root || !sentinel || this.produtosEncontrados.length === 0) return
      if (this.paginaAtual >= this.totalPaginas) return

      this.observerListaProdutos = new IntersectionObserver(
        entries => {
          entries.forEach(e => {
            if (e.isIntersecting && this.paginaAtual < this.totalPaginas) {
              this.proximaPagina()
            }
          })
        },
        { root, rootMargin: '120px', threshold: 0 },
      )
      this.observerListaProdutos.observe(sentinel)
    },
    teardownInfiniteScrollProdutos() {
      if (this.observerListaProdutos) {
        this.observerListaProdutos.disconnect()
        this.observerListaProdutos = null
      }
    },
    getStore() {
      this.store = this.$store.state.store
    },
    async buscarProdutos() {
      if (!this.termoBusca || this.termoBusca.trim() === '') {
        this.$bvToast.toast('Digite um termo para buscar', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.buscandoProdutos = true
      this.buscaJaRealizada = true
      this.produtosEncontrados = []
      this.paginaAtual = 1
      try {
        const tipoBusca = this.buscarPorSku ? 'sku' : 'name'
        this.produtosEncontrados = await buscarProdutos(this.termoBusca, this.uuid, tipoBusca)

        if (this.produtosEncontrados.length === 0) {
          this.$bvToast.toast('Nenhum produto encontrado', {
            title: 'Busca',
            variant: 'info',
            solid: true,
          })
        }
      } catch (error) {
        console.error('Erro ao buscar produtos:', error)
        const mensagem = (error && error.message) ? error.message : 'Erro ao buscar produtos.'
        this.$bvToast.toast(mensagem, {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.buscandoProdutos = false
        this.$nextTick(() => this.setupInfiniteScrollProdutos())
      }
    },
    proximaPagina() {
      if (this.paginaAtual < this.totalPaginas) {
        this.paginaAtual += 1
      }
    },
    getLinkCompleto(link) {
      if (!link) return null
      if (link.startsWith('http://') || link.startsWith('https://')) {
        return link
      }
      return `${this.store.site_url}${link.startsWith('/') ? '' : '/'}${link}`
    },
    ajustarQtdBusca(produtoId, delta) {
      const cur = this.qtdAdicionarBusca[produtoId] || 1
      const next = Math.max(1, cur + delta)
      this.$set(this.qtdAdicionarBusca, produtoId, next)
    },
    adicionarAoCarrinho(produto) {
      const qtdDesejada = Math.max(1, parseInt(this.qtdAdicionarBusca[produto.id], 10) || 1)
      const jaExiste = this.carrinho.find(p => p.id === produto.id)
      if (jaExiste) {
        jaExiste.quantidade = (jaExiste.quantidade || 1) + qtdDesejada
        const atualizado = this.montarItemProdutoCarrinho({ ...jaExiste, ...produto }, jaExiste.quantidade)
        this.$set(jaExiste, 'imagem', atualizado.imagem)
        this.$delete(this.imgCarrinhoQuebrada, produto.id)
        this.$bvToast.toast(`Quantidade de ${produto.nome} aumentada para ${jaExiste.quantidade}`, {
          title: 'Quantidade Atualizada',
          variant: 'info',
          solid: true,
        })
        this.$set(this.qtdAdicionarBusca, produto.id, 1)
        return
      }

      this.carrinho.push(this.montarItemProdutoCarrinho(produto, qtdDesejada))
      this.$set(this.qtdAdicionarBusca, produto.id, 1)
      this.$bvToast.toast(`${produto.nome} adicionado ao carrinho`, {
        title: 'Sucesso',
        variant: 'success',
        solid: true,
      })
    },
    aumentarQuantidade(produtoId) {
      const produto = this.carrinho.find(p => p.id === produtoId)
      if (produto) {
        produto.quantidade = (produto.quantidade || 1) + 1
      }
    },
    diminuirQuantidade(produtoId) {
      const produto = this.carrinho.find(p => p.id === produtoId)
      if (produto) {
        if (produto.quantidade > 1) {
          produto.quantidade -= 1
        } else {
          this.removerDoCarrinho(produtoId)
        }
      }
    },
    removerDoCarrinho(produtoId) {
      this.carrinho = this.carrinho.filter(p => p.id !== produtoId)
      this.$delete(this.imgCarrinhoQuebrada, produtoId)
      this.$bvToast.toast('Produto removido do carrinho', {
        title: 'Removido',
        variant: 'info',
        solid: true,
      })
    },
    limparCarrinho() {
      this.carrinho = []
      this.cepCliente = ''
      this.cupomDesconto = ''
      this.cupomTemDesconto = false
      this.cupomPercentualDesconto = ''
      this.imgCarrinhoQuebrada = {}
      this.$bvToast.toast('Carrinho limpo', {
        title: 'Limpo',
        variant: 'info',
        solid: true,
      })
    },
    formatarCEP() {
      let cep = this.cepCliente.replace(/\D/g, '')

      cep = cep.substring(0, 8)

      if (cep.length <= 5) {
        this.cepCliente = cep
      } else {
        this.cepCliente = `${cep.substring(0, 5)}-${cep.substring(5)}`
      }
    },
    copiarLink() {
      if (!this.linkCarrinhoGerado) {
        this.$bvToast.toast('Nenhum link para copiar', {
          title: 'Aviso',
          variant: 'warning',
          solid: true,
        })
        return
      }

      navigator.clipboard.writeText(this.linkCarrinhoGerado).then(() => {
        this.$bvToast.toast('Link copiado para a área de transferência!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })
      }).catch(err => {
        console.error('Erro ao copiar:', err)
        this.$bvToast.toast('Erro ao copiar link', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      })
    },
    abrirLink() {
      if (!this.linkCarrinhoGerado) {
        this.$bvToast.toast('Nenhum link para abrir', {
          title: 'Aviso',
          variant: 'warning',
          solid: true,
        })
        return
      }

      window.open(this.linkCarrinhoGerado, '_blank')
    },
    fecharModal() {
      this.showModal = false
    },
    /**
     * Envia o texto da prévia para o composer do chat aberto.
     * O pai (`CentralAtendimento`) repassa ao `ChatAtendimento`.
     * Com `enviarAutoChat`, a mensagem é enviada automaticamente após preencher o campo.
     */
    enviarCarrinhoWhatsApp() {
      if (!this.linkCarrinhoGerado || this.carrinho.length === 0) return
      const texto = (this.textoPreviewMensagem || '').trim()
      if (!texto) {
        this.$bvToast.toast('Monte a prévia da mensagem antes de enviar ao chat.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }
      this.$emit('enviar-ao-chat', {
        texto: this.textoPreviewMensagem,
        enviarAutomatico: !!this.enviarAutoChat,
      })
    },
    getChaveCache() {
      return `carrinho_cliente_${this.clienteId}`
    },
    salvarCarrinhoNoCache() {
      const temCarrinho = this.carrinho.length > 0

      if (!temCarrinho) {
        localStorage.removeItem(this.getChaveCache())
        console.log('🗑️ Carrinho vazio, cache removido para cliente:', this.clienteId)
        return
      }

      const dadosCarrinho = {
        carrinho: this.carrinho.map(p => this.montarItemProdutoCarrinho(p, p.quantidade || 1)),
        cepCliente: this.cepCliente,
        cupomDesconto: this.cupomDesconto,
        cupomTemDesconto: this.cupomTemDesconto,
        cupomPercentualDesconto: this.cupomPercentualDesconto,
        timestamp: new Date().toISOString(),
      }

      localStorage.setItem(this.getChaveCache(), JSON.stringify(dadosCarrinho))
      console.log('💾 Carrinho salvo no cache para cliente:', this.clienteId, dadosCarrinho)
    },
    carregarMensagemGlobal() {
      const msgGlobal = localStorage.getItem('carrinho_mensagem_global')
      this.mensagemCustomizadaCarrinho = typeof msgGlobal === 'string' ? msgGlobal : ''
    },
    restaurarCarrinhoDoCache() {
      this.carregarMensagemGlobal()

      const chave = this.getChaveCache()
      const dadosSalvos = localStorage.getItem(chave)

      if (dadosSalvos) {
        try {
          const dados = JSON.parse(dadosSalvos)
          const lista = dados.carrinho || []
          this.carrinho = lista.length
            ? lista.map(p => this.montarItemProdutoCarrinho(p, p.quantidade || 1))
            : []
          this.cepCliente = dados.cepCliente || ''
          this.cupomDesconto = dados.cupomDesconto || ''
          this.cupomTemDesconto = !!dados.cupomTemDesconto
          this.cupomPercentualDesconto = dados.cupomPercentualDesconto != null && dados.cupomPercentualDesconto !== ''
            ? String(dados.cupomPercentualDesconto)
            : ''
          this.imgCarrinhoQuebrada = {}
          console.log('📂 Carrinho restaurado do cache para cliente:', this.clienteId, dados)
        } catch (error) {
          console.error('Erro ao restaurar carrinho do cache:', error)
          this.carrinho = []
          this.cepCliente = ''
          this.cupomDesconto = ''
          this.cupomTemDesconto = false
          this.cupomPercentualDesconto = ''
          this.imgCarrinhoQuebrada = {}
        }
      } else {
        this.carrinho = []
        this.cepCliente = ''
        this.cupomDesconto = ''
        this.cupomTemDesconto = false
        this.cupomPercentualDesconto = ''
        this.produtosEncontrados = []
        this.termoBusca = ''
      }
    },
    limparCacheCliente() {
      localStorage.removeItem(this.getChaveCache())
      this.carrinho = []
      this.cepCliente = ''
      this.cupomDesconto = ''
      this.cupomTemDesconto = false
      this.cupomPercentualDesconto = ''
      this.produtosEncontrados = []
      this.termoBusca = ''
    },
  },
}
</script>

<style>
/* Modal Bootstrap: tamanho e corpo sem padding extra do tema */
.mc-carrinho-dialog {
  max-width: 90vw !important;
  width: 90vw !important;
  margin: 1.75rem auto;
}

.mc-carrinho-content-wrap {
  border-radius: 14px;
  border: 1px solid #e8e6f0;
  box-shadow: 0 4px 24px rgba(40, 39, 65, 0.08);
  overflow: hidden;
  background: #fff;
}

.mc-carrinho-modal-body.modal-body-carrinho {
  padding: 0 !important;
  max-height: 90vh;
  overflow: hidden;
}
</style>

<style scoped>
.mc-carrinho-root {
  display: flex;
  flex-direction: column;
  max-height: min(90vh, 900px);
  background: #fff;
}

/* —— Header —— */
.mc-carrinho-header {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid #e8e6f0;
  background: #fff;
}

.mc-carrinho-header-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
}

.mc-carrinho-header-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.mc-carrinho-header-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(115, 103, 240, 0.1);
  color: #7367f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex-shrink: 0;
}

.mc-carrinho-header-texts {
  min-width: 0;
}

.mc-carrinho-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #2c2c45;
  line-height: 1.25;
}

.mc-carrinho-subtitle {
  margin: 0.15rem 0 0;
  font-size: 0.78rem;
  color: #6e6b86;
}

.mc-carrinho-header-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
  flex-shrink: 0;
}

.mc-badge {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 8px;
  background: #f0eefc;
  color: #5e54d1;
  border: 1px solid #e8e6f0;
}

.mc-badge-items {
  text-transform: none;
}

.mc-header-total {
  font-size: 0.95rem;
  font-weight: 700;
  color: #28a745;
}

.mc-carrinho-header-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
}

.mc-carrinho-header-actions .btn {
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.78rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.mc-btn-outline-danger {
  border-color: #e8e6f0 !important;
  color: #c5283b !important;
  background: #fff !important;
}

.mc-btn-outline-danger:hover:not(:disabled) {
  background: #fff5f5 !important;
  border-color: #f5c6cb !important;
}

.mc-btn-outline-copy {
  border-color: #e8e6f0 !important;
  color: #5e54d1 !important;
}

.mc-btn-cta-header {
  background: #7367f0 !important;
  border-color: #7367f0 !important;
}

.mc-carrinho-close {
  position: absolute;
  top: 0.65rem;
  right: 0.65rem;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 10px;
  background: #fafafc;
  color: #6e6b86;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.mc-carrinho-close:hover {
  background: #f0eefc;
  color: #7367f0;
}

/* —— Grid —— */
.mc-carrinho-grid {
  display: grid;
  grid-template-columns: minmax(0, 62%) minmax(0, 38%);
  flex: 1;
  min-height: 0;
}

.mc-carrinho-col {
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.mc-carrinho-col-left {
  padding: 1rem 1rem 1rem 1.25rem;
  border-right: 1px solid #e8e6f0;
}

.mc-carrinho-col-right {
  background: #fafafc;
  border-left: 1px solid #e8e6f0;
  margin-left: -1px;
}

.mc-right-inner {
  padding: 1rem 1.25rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  max-height: calc(min(90vh, 900px) - 72px);
}

/* Busca */
.mc-search-block {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mc-search-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.5rem 0.35rem 0.75rem;
  border: 1px solid #e8e6f0;
  border-radius: 12px;
  background: #fafafc;
}

.mc-search-icon {
  color: #9b97b5;
  flex-shrink: 0;
}

.mc-search-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: 0.875rem;
  outline: none;
  color: #2c2c45;
}

.mc-search-input::placeholder {
  color: #9b97b5;
}

.mc-search-submit {
  border-radius: 10px !important;
  flex-shrink: 0;
}

.mc-sku-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  font-size: 0.78rem;
  color: #6e6b86;
  align-self: flex-start;
}

.mc-sku-toggle-track {
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: #e8e6f0;
  position: relative;
  transition: background 0.2s;
}

.mc-sku-toggle--on .mc-sku-toggle-track {
  background: #7367f0;
}

.mc-sku-toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  transition: transform 0.2s;
}

.mc-sku-toggle--on .mc-sku-toggle-thumb {
  transform: translateX(16px);
}

/* Chips */
.mc-chips-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.25rem;
}

.mc-chip {
  border: 1px solid #e8e6f0;
  background: #fff;
  color: #6e6b86;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.mc-chip:hover {
  border-color: #d4cfee;
}

.mc-chip--active {
  background: rgba(115, 103, 240, 0.12);
  border-color: #c4bdf5;
  color: #5e54d1;
}

.mc-resultados-hint {
  margin: 0.5rem 0 0.25rem;
  font-size: 0.72rem;
  color: #9b97b5;
}

.mc-lista-produtos-scroll {
  flex: 1;
  min-height: 220px;
  max-height: 420px;
  overflow-y: auto;
  margin-top: 0.25rem;
  padding-right: 0.25rem;
}

/* Skeleton */
.mc-prod-skeleton {
  display: flex;
  gap: 0.75rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid #f0eefc;
}

.mc-prod-skeleton-img {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f0eefc 25%, #fafafc 50%, #f0eefc 75%);
  background-size: 200% 100%;
  animation: mc-shimmer 1.2s infinite;
}

.mc-prod-skeleton-lines {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  justify-content: center;
}

.mc-prod-skeleton-line {
  height: 10px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0eefc 25%, #fafafc 50%, #f0eefc 75%);
  background-size: 200% 100%;
  animation: mc-shimmer 1.2s infinite;
}

.mc-prod-skeleton-line--lg {
  width: 85%;
}

.mc-prod-skeleton-line--sm {
  width: 45%;
}

@keyframes mc-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Linha produto */
.mc-prod-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid #f0eefc;
}

.mc-prod-row-img {
  width: 52px;
  height: 52px;
  object-fit: cover;
  border-radius: 10px;
  flex-shrink: 0;
  border: 1px solid #e8e6f0;
}

.mc-prod-row-body {
  flex: 1;
  min-width: 0;
}

.mc-prod-row-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: #2c2c45;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mc-prod-row-name--link {
  color: #2c2c45;
  text-decoration: none;
}

.mc-prod-row-name--link:hover {
  color: #7367f0;
}

.mc-prod-row-sku {
  font-size: 0.68rem;
  color: #9b97b5;
  margin-top: 0.15rem;
}

.mc-prod-row-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.2rem;
  flex-wrap: wrap;
}

.mc-prod-row-preco {
  font-size: 0.82rem;
  font-weight: 700;
  color: #28a745;
}

.mc-prod-row-estoque {
  font-size: 0.68rem;
  color: #28a745;
}

.mc-prod-row-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  flex-shrink: 0;
}

.mc-qty-inline {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  border: 1px solid #e8e6f0;
  border-radius: 8px;
  padding: 0.1rem;
  background: #fff;
}

.mc-qty-inline-btn {
  width: 26px;
  height: 26px;
  border: none;
  background: #fafafc;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6e6b86;
}

.mc-qty-inline-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mc-qty-inline-val {
  min-width: 1.25rem;
  text-align: center;
  font-size: 0.78rem;
  font-weight: 600;
  color: #2c2c45;
}

.mc-btn-add {
  border-radius: 10px !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  padding: 0.25rem 0.6rem !important;
}

.mc-sentinel-load {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  font-size: 0.75rem;
  color: #9b97b5;
}

.mc-sentinel-spinner {
  color: #7367f0 !important;
}

/* Carrinho direita */
.mc-cart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.mc-cart-title {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: #2c2c45;
}

.mc-link-quiet {
  border: none;
  background: none;
  font-size: 0.75rem;
  font-weight: 600;
  color: #c5283b;
  cursor: pointer;
  padding: 0;
}

.mc-link-quiet:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.mc-produtos-no-carrinho-wrap {
  margin-top: 0.25rem;
}

.mc-produtos-no-carrinho-titulo {
  margin: 0 0 0.5rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #9b97b5;
}

.mc-empty-produtos-carrinho {
  padding: 1rem 0.75rem;
  text-align: center;
  background: #fff;
  border: 1px dashed #e8e6f0;
  border-radius: 12px;
}

.mc-empty-produtos-carrinho-title {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 700;
  color: #2c2c45;
}

.mc-empty-produtos-carrinho-hint {
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  color: #9b97b5;
}

.mc-cart-produtos-scroll {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 260px;
  overflow-y: auto;
  padding-right: 0.15rem;
}

.mc-prod-carrinho-row {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.55rem;
  background: #fff;
  border: 1px solid #e8e6f0;
  border-radius: 12px;
}

.mc-prod-carrinho-img {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 10px;
  flex-shrink: 0;
  border: 1px solid #f0eefc;
}

.mc-prod-carrinho-img--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0eefc;
  border: 1px dashed #d4cfee;
  color: #9b97b5;
  font-size: 1.15rem;
  object-fit: none;
}

.mc-prod-carrinho-body {
  flex: 1;
  min-width: 0;
}

.mc-prod-carrinho-name {
  font-size: 0.78rem;
  font-weight: 600;
  color: #2c2c45;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mc-prod-carrinho-name--link {
  text-decoration: none;
  color: #2c2c45;
}

.mc-prod-carrinho-name--link:hover {
  color: #7367f0;
}

.mc-prod-carrinho-sku {
  font-size: 0.68rem;
  color: #9b97b5;
  margin-top: 0.15rem;
}

.mc-prod-carrinho-preco-linha {
  font-size: 0.78rem;
  font-weight: 600;
  color: #28a745;
  margin-top: 0.2rem;
}

.mc-prod-carrinho-subtotal {
  font-size: 0.72rem;
  font-weight: 700;
  color: #2c2c45;
  margin-top: 0.15rem;
}

.mc-prod-carrinho-aside {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  flex-shrink: 0;
}

.mc-preview-msg-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.mc-section-label--preview {
  margin-bottom: 0 !important;
  flex: 1;
}

.mc-btn-editar-msg {
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  padding: 0.15rem 0.35rem !important;
  text-decoration: none !important;
  color: #7367f0 !important;
  white-space: nowrap;
}

.mc-btn-editar-msg:hover {
  color: #5e54d1 !important;
}

.mc-mini-badge--cep {
  background: #e8f4fc;
  color: #1a6fa8;
  border-color: #c5e3f5;
}

.mc-edit-msg-hint {
  font-size: 0.78rem;
  color: #6e6b86;
  margin-bottom: 0.65rem;
}

.mc-edit-msg-hint-lead {
  margin: 0 0 0.5rem;
  line-height: 1.45;
}

.mc-edit-msg-hint-lead strong {
  font-weight: 600;
  color: #2c2c45;
}

.mc-edit-msg-hint-sub {
  margin: 0 0 0.35rem;
  font-weight: 600;
  color: #2c2c45;
  font-size: 0.8rem;
}

.mc-edit-msg-vars-list {
  margin: 0 0 0.55rem;
  padding-left: 1.1rem;
  line-height: 1.45;
}

.mc-edit-msg-vars-item {
  margin-bottom: 0.35rem;
}

.mc-edit-msg-var-code {
  display: inline-block;
  margin-right: 0.35rem;
  padding: 0.1rem 0.35rem;
  font-size: 0.72rem;
  background: #f4f2fa;
  border-radius: 6px;
  color: #5e54d1;
  vertical-align: baseline;
}

.mc-edit-msg-var-desc {
  font-size: 0.76rem;
  color: #6e6b86;
}

.mc-edit-msg-hint-note {
  margin: 0;
  font-size: 0.74rem;
  color: #8a869e;
  line-height: 1.45;
}

.mc-edit-msg-textarea {
  font-size: 0.82rem !important;
  border-radius: 10px !important;
  border-color: #e8e6f0 !important;
}

.mc-edit-msg-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e8e6f0;
}

/* Carrinho direita — legado removido (.mc-cart-card) em favor de .mc-prod-carrinho-row */

.mc-qty-inline--cart {
  border: none;
  padding: 0;
  gap: 0.15rem;
}

.mc-qty-btn {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  border-radius: 8px !important;
  border-color: #e8e6f0 !important;
}

.mc-qty-num {
  min-width: 1.1rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.78rem;
}

.mc-cart-remove {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  background: #fff5f5;
  color: #c5283b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mc-cart-remove:hover {
  background: #ffe5e8;
}

/* Resumo */
.mc-resumo-pedido {
  padding: 0.75rem;
  background: #fff;
  border: 1px solid #e8e6f0;
  border-radius: 12px;
}

.mc-section-label {
  margin: 0 0 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #9b97b5;
}

.mc-resumo-line {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #2c2c45;
  padding: 0.2rem 0;
}

.mc-resumo-muted {
  color: #9b97b5;
  font-size: 0.75rem;
}

.mc-resumo-desconto {
  color: #c0392b;
  font-weight: 600;
}

.mc-cupom-desconto-extra {
  margin-top: 0.5rem;
  padding-top: 0.65rem;
  border-top: 1px solid #f0eef5;
}

.mc-checkbox-cupom-desconto {
  font-size: 0.82rem;
  color: #2c2c45;
  margin-bottom: 0.35rem;
}

.mc-field--pct .mc-field-input {
  padding-left: 0.75rem !important;
}

.mc-resumo-total {
  margin-top: 0.35rem;
  padding-top: 0.45rem;
  border-top: 1px solid #e8e6f0;
  font-weight: 700;
  font-size: 0.95rem;
}

.mc-resumo-total span:last-child {
  color: #28a745;
  font-size: 1.05rem;
}

.mc-section {
  margin: 0;
}

.mc-field {
  margin-bottom: 0.75rem;
}

.mc-field:last-child {
  margin-bottom: 0;
}

.mc-field-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #2c2c45;
  margin-bottom: 0.3rem;
}

.mc-field-input-wrap {
  position: relative;
}

.mc-field-icon {
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9b97b5;
  font-size: 0.9rem;
  pointer-events: none;
  z-index: 1;
}

.mc-field-input {
  padding-left: 2.1rem !important;
  border-radius: 10px !important;
  border-color: #e8e6f0 !important;
  font-size: 0.82rem !important;
}

.mc-field-hint {
  margin: 0.25rem 0 0;
  font-size: 0.68rem;
  color: #9b97b5;
}

.mc-link-preview-box {
  background: #fff;
  border: 1px solid #e8e6f0;
  border-radius: 12px;
  padding: 0.6rem 0.65rem;
}

.mc-link-url {
  font-size: 0.72rem;
  color: #5e54d1;
  word-break: break-all;
  line-height: 1.35;
  max-height: 4.2rem;
  overflow-y: auto;
  font-family: ui-monospace, monospace;
}

.mc-link-mini-actions {
  display: flex;
  gap: 0.35rem;
  margin-top: 0.5rem;
}

.mc-link-mini-actions .btn {
  border-radius: 8px !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
}

.mc-link-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.5rem;
}

.mc-mini-badge {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.2rem 0.45rem;
  border-radius: 8px;
  background: #f0eefc;
  color: #5e54d1;
  border: 1px solid #e8e6f0;
}

.mc-mini-badge--cupom {
  background: #e8f8ef;
  color: #1e7a4a;
  border-color: #c3e6d0;
}

/* WhatsApp preview */
.mc-section--preview {
  padding-top: 0.25rem;
}

.mc-wa-preview {
  margin-bottom: 0.5rem;
}

.mc-wa-bubble {
  background: #e7fce3;
  border-radius: 12px 12px 12px 4px;
  padding: 0.65rem 0.75rem;
  max-width: 100%;
  border: 1px solid #cfe9c8;
}

.mc-wa-bubble-text {
  margin: 0;
  font-family: inherit;
  font-size: 0.78rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  color: #1a3d16;
}

.mc-checkbox-auto {
  font-size: 0.78rem;
}

.mc-btn-wa-footer {
  border-radius: 12px !important;
  font-weight: 700 !important;
  margin-top: auto;
  background: #7367f0 !important;
  border: none !important;
}

.mc-btn-wa-footer:disabled {
  opacity: 0.45;
}

/* Empty */
.mc-empty-state {
  text-align: center;
  padding: 1.5rem 0.75rem;
}

.mc-empty-state i {
  font-size: 2rem;
  color: #d4cfee;
  margin-bottom: 0.35rem;
}

.mc-empty-state--produtos i {
  color: #d4cfee;
}

.mc-empty-title {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: #2c2c45;
}

.mc-empty-hint {
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  color: #9b97b5;
}

/* Scrollbar lista */
.mc-lista-produtos-scroll::-webkit-scrollbar,
.mc-cart-produtos-scroll::-webkit-scrollbar,
.mc-right-inner::-webkit-scrollbar,
.mc-link-url::-webkit-scrollbar {
  width: 5px;
}

.mc-lista-produtos-scroll::-webkit-scrollbar-thumb,
.mc-cart-produtos-scroll::-webkit-scrollbar-thumb,
.mc-right-inner::-webkit-scrollbar-thumb {
  background: #d4cfee;
  border-radius: 4px;
}

/* Responsivo */
@media (max-width: 991px) {
  .mc-carrinho-grid {
    grid-template-columns: 1fr;
    overflow-y: auto;
    max-height: calc(min(90vh, 900px) - 72px);
  }

  .mc-carrinho-col-left {
    border-right: none;
    border-bottom: 1px solid #e8e6f0;
    max-height: none;
  }

  .mc-carrinho-col-right {
    border-left: none;
    margin-left: 0;
  }

  .mc-lista-produtos-scroll {
    max-height: 320px;
  }

  .mc-right-inner {
    max-height: none;
    overflow-y: visible;
  }

  .mc-carrinho-header-main {
    flex-direction: column;
    align-items: flex-start;
  }

  .mc-carrinho-header-actions {
    width: 100%;
  }
}

@media (max-width: 576px) {
  .mc-carrinho-dialog {
    width: 96vw !important;
    max-width: 96vw !important;
  }

  .mc-prod-row {
    flex-wrap: wrap;
  }

  .mc-prod-row-actions {
    width: 100%;
    flex-direction: row;
    justify-content: flex-end;
  }
}
</style>
